import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ZarrLayer,
  ZarrLayerOptions,
  QueryGeometry,
} from '@carbonplan/zarr-layer'
import maplibregl from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useAppStore, MapInstance } from '../store'
import type { LayerProps } from '../datasets/types'
import { COLORMAPS } from '../../../components/ZarrMapViewer/colormaps'
import { TimeSeriesChartOverlay } from './TimeSeriesChart'
import TimeSlider from './TimeSlider'
import { DatasetChip, MapColorbar, OpenInNewWindowButton } from './MapOverlays'
import DatasetSwitchNotice from './DatasetSwitchNotice'
import MapStatus from './MapStatus'

const BASEMAP_STYLE = {
  version: 8 as const,
  sources: {
    osm: {
      type: 'raster' as const,
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      attribution: '© OpenStreetMap contributors',
    },
  },
  layers: [{ id: 'osm', type: 'raster' as const, source: 'osm' }],
}

export const useMapLayer = (map: MapInstance | null, isMapLoaded: boolean) => {
  const zarrLayerRef = useRef<InstanceType<typeof ZarrLayer> | null>(null)
  const prevDatasetIdRef = useRef<string | null>(null)
  const datasetId = useAppStore((state) => state.datasetId)
  const datasetModule = useAppStore((state) => state.getDatasetModule())
  const datasetState = useAppStore((state) => state.datasetState)
  const opacity = useAppStore((state) => state.opacity)
  const clim = useAppStore((state) => state.clim)
  const colormap = useAppStore((state) => state.colormap)
  const setLoadingState = useAppStore((state) => state.setLoadingState)
  const colormapArray = COLORMAPS[colormap] ?? COLORMAPS['eodc']
  const setPointResult = useAppStore((state) => state.setPointResult)
  const setZarrLayer = useAppStore((state) => state.setZarrLayer)
  const hoverQueryEnabled = useAppStore((state) => state.hoverQueryEnabled)
  const zarrLayerHidden = useAppStore((state) => state.zarrLayerHidden)
  const setTimeStepsPerDay = useAppStore((state) => state.setTimeStepsPerDay)
  const setFormatTimeIndex = useAppStore((state) => state.setFormatTimeIndex)
  const setReverseTimeIndex = useAppStore((state) => state.setReverseTimeIndex)

  const layerConfig: LayerProps = useMemo(
    () => datasetModule.buildLayerProps(datasetState),
    [datasetModule, datasetState]
  )

  useEffect(() => {
    if (!map || !isMapLoaded) return

    let clickHandler: ((event: any) => void) | null = null
    let cancelled = false

    if (zarrLayerRef.current) {
      try {
        if ((map as any).getLayer('zarr-layer')) {
          (map as any).removeLayer('zarr-layer')
        }
      } catch (e) {}
      zarrLayerRef.current = null
    }

    const createLayer = async () => {
      const currentLayerConfig = datasetModule.buildLayerProps(
        useAppStore.getState().datasetState
      )
      const options: ZarrLayerOptions = {
        id: 'zarr-layer',
        source: datasetModule.source,
        variable: currentLayerConfig.variable ?? datasetModule.variable,
        clim: clim,
        colormap: colormapArray,
        opacity: opacity,
        selector: currentLayerConfig.selector,
        zarrVersion: datasetModule.zarrVersion,
        fillValue: datasetModule.fillValue,
        spatialDimensions: datasetModule.spatialDimensions,
        bounds: datasetModule.bounds,
        latIsAscending: datasetModule.latIsAscending,
        proj4: datasetModule.proj4,
        onLoadingStateChange: setLoadingState,
        renderPoles: false,
      }

      if (datasetModule.store) {
        options.store = await datasetModule.store
      }

      if (cancelled) return

      const latestState = useAppStore.getState()
      options.clim = latestState.clim
      options.opacity = latestState.opacity
      options.colormap = COLORMAPS[latestState.colormap] ?? COLORMAPS['eodc']

      const latestConfig = datasetModule.buildLayerProps(
        latestState.datasetState
      )
      options.selector = latestConfig.selector
      if (latestConfig.customFrag) {
        options.customFrag = latestConfig.customFrag
      }
      if (latestConfig.uniforms) {
        options.uniforms = latestConfig.uniforms
      }

      const layer = new ZarrLayer(options)
      let beforeId: string | undefined

      ;(map as any).addLayer(layer, beforeId)

      clickHandler = (event: any) => {
        const geometry: QueryGeometry = {
          type: 'Point',
          coordinates: [event.lngLat.lng, event.lngLat.lat],
        }
        const appState = useAppStore.getState()
        const querySelector = datasetModule.buildLayerProps(
          appState.datasetState
        ).selector

        if (appState.timeSeriesModeEnabled) {
          const timeDim = datasetModule.timeDimension ?? 'time'
          const { [timeDim]: _t, ...selectorWithoutTime } = querySelector as any
          const currentTime =
            typeof appState.datasetState.time === 'number'
              ? appState.datasetState.time
              : 0
          const timeOpts: Record<string, unknown> = {
            selector: selectorWithoutTime,
          }
          const useMeanRange =
            appState.timeSeriesUseMeanRange &&
            appState.timeMeanStartDate &&
            appState.timeMeanEndDate &&
            appState.reverseTimeIndex
          if (useMeanRange) {
            const startIdx = appState.reverseTimeIndex!(appState.timeMeanStartDate!)
            const endIdx = appState.reverseTimeIndex!(appState.timeMeanEndDate!)
            if (Number.isFinite(startIdx) && Number.isFinite(endIdx)) {
              timeOpts.start = Math.max(0, Math.round(startIdx))
              timeOpts.end = Math.round(endIdx)
            }
          } else if (appState.timeSeriesWindow !== 'all') {
            const windowSteps =
              appState.timeSeriesWindow * appState.timeStepsPerDay
            timeOpts.start = Math.max(
              0,
              currentTime - Math.floor(windowSteps / 2)
            )
            timeOpts.end = currentTime + Math.ceil(windowSteps / 2)
          }
          useAppStore.getState().setTimeSeriesLoading(true)
          const queryPromise = layer.queryTimeSeries(geometry, timeOpts as any)
          queryPromise
            .then((result) => {
              if (cancelled) return
              useAppStore.getState().setTimeSeriesResult(result)
            })
            .catch((err) => {
              console.error('[ts-click] queryTimeSeries failed:', err)
            })
            .finally(() => {
              if (!cancelled) useAppStore.getState().setTimeSeriesLoading(false)
            })
        } else {
          layer.queryData(geometry, querySelector).then((result) => {
            if (cancelled) return
            setPointResult(result)
          })
        }
      }
      ;(map as any).on('click', clickHandler)
      zarrLayerRef.current = layer
      setZarrLayer(layer)

      // Only center the map on the dataset's default view on the *first*
      // dataset load. When the user switches datasets in global mode, keep
      // whatever viewport they already had so the switch feels like a layer
      // swap rather than a full context reset.
      if (
        datasetModule.center &&
        prevDatasetIdRef.current === null
      ) {
        ;(map as any).easeTo({
          center: datasetModule.center,
          zoom: datasetModule.zoom || 4,
        })
      }
      prevDatasetIdRef.current = datasetId
    }

    createLayer().catch((error) => {
      console.error('Error creating ZarrLayer:', error)
    })

    return () => {
      cancelled = true
      if (zarrLayerRef.current) {
        try {
          if ((map as any).getLayer('zarr-layer')) {
            (map as any).removeLayer('zarr-layer')
          }
        } catch (e) {}
        setZarrLayer(null)
        zarrLayerRef.current = null
      }
      if (clickHandler && (map as any).off) {
        try {
          ;(map as any).off('click', clickHandler)
        } catch (e) {}
      }
    }
  }, [
    map,
    isMapLoaded,
    datasetId,
    datasetModule,
    layerConfig.customFrag,
    layerConfig.variable,
    setLoadingState,
  ])

  useEffect(() => {
    if (!map || !isMapLoaded || !hoverQueryEnabled) return

    let abortController: AbortController | null = null
    let cancelled = false

    const canvas: HTMLCanvasElement | undefined = (map as any).getCanvas?.()
    const prevCursor = canvas?.style.cursor
    if (canvas) canvas.style.cursor = 'crosshair'

    const handler = (event: any) => {
      const layer = zarrLayerRef.current
      if (!layer) return

      abortController?.abort()
      abortController = new AbortController()
      const thisController = abortController

      const geometry: QueryGeometry = {
        type: 'Point',
        coordinates: [event.lngLat.lng, event.lngLat.lat],
      }
      const querySelector = datasetModule.buildLayerProps(
        useAppStore.getState().datasetState
      ).selector

      layer
        .queryData(geometry, querySelector, { signal: thisController.signal })
        .then((result) => {
          if (cancelled || thisController.signal.aborted) return
          setPointResult(result)
        })
        .catch((err) => {
          if (err instanceof DOMException && err.name === 'AbortError') return
          console.warn('Hover query failed', err)
        })
    }

    ;(map as any).on('mousemove', handler)

    return () => {
      cancelled = true
      abortController?.abort()
      if (canvas) canvas.style.cursor = prevCursor ?? ''
      try {
        ;(map as any).off('mousemove', handler)
      } catch (e) {}
    }
  }, [map, isMapLoaded, hoverQueryEnabled, datasetModule, setPointResult])

  useEffect(() => {
    setTimeStepsPerDay(datasetModule.timeStepsPerDay ?? 1)
    setFormatTimeIndex(datasetModule.formatTimeIndex ?? null)
    setReverseTimeIndex(datasetModule.reverseTimeIndex ?? null)
  }, [
    datasetId,
    datasetModule,
    setTimeStepsPerDay,
    setFormatTimeIndex,
    setReverseTimeIndex,
  ])

  useEffect(() => {
    const layer = zarrLayerRef.current
    if (!layer || !map || !isMapLoaded) return

    layer.setOpacity(zarrLayerHidden ? 0 : opacity)
    layer.setColormap(colormapArray)
    layer.setClim(clim)
    layer.setSelector(layerConfig.selector)

    if (layerConfig.uniforms && Object.keys(layerConfig.uniforms).length > 0) {
      layer.setUniforms(layerConfig.uniforms)
    }
  }, [
    opacity,
    zarrLayerHidden,
    clim,
    colormapArray,
    layerConfig,
    map,
    isMapLoaded,
  ])

  return zarrLayerRef
}

export const Map = () => {
  const mapContainer = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<maplibregl.Map | null>(null)
  const [map, setMap] = useState<maplibregl.Map | null>(null)
  const [isMapLoaded, setIsMapLoaded] = useState(false)

  const sidebarWidth = useAppStore((state) => state.sidebarWidth)
  const rightRailWidth = useAppStore((state) => state.rightRailWidth)
  const loadingState = useAppStore((state) => state.loadingState)
  const timeMeanLoading = useAppStore((state) => state.timeMeanLoading)
  const timeSeriesLoading = useAppStore((state) => state.timeSeriesLoading)
  const setMapInstance = useAppStore((state) => state.setMapInstance)
  const globeProjection = useAppStore((state) => state.globeProjection)
  const timeCount = useAppStore((state) => state.timeCount)
  const setTimeSliderHeight = useAppStore((state) => state.setTimeSliderHeight)
  const panelRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!mapContainer.current) return

    const newMap = new maplibregl.Map({
      container: mapContainer.current,
      style: BASEMAP_STYLE as any,
      center: [15, 48],
      zoom: 4,
    })

    mapInstanceRef.current = newMap

    newMap.on('load', () => {
      setMap(newMap)
      setIsMapLoaded(true)
      setMapInstance(newMap as any)
      try {
        ;(newMap as any).setProjection({
          type: useAppStore.getState().globeProjection ? 'globe' : 'mercator',
        })
      } catch (e) {
        console.warn('Projection switch not supported:', e)
      }
    })

    return () => {
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove()
        } catch (error) {
          console.warn('Error removing map:', error)
        }
        setMapInstance(null)
        mapInstanceRef.current = null
      }
    }
  }, [])

  useMapLayer(map as any, isMapLoaded)

  // Measure the total panel height (slider + chart) so MapColorbar stays above it.
  useEffect(() => {
    const el = panelRef.current
    if (!el) return
    let rafId = 0
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => setTimeSliderHeight(el.offsetHeight))
    })
    ro.observe(el)
    setTimeSliderHeight(el.offsetHeight)
    return () => {
      cancelAnimationFrame(rafId)
      ro.disconnect()
      setTimeSliderHeight(0)
    }
  }, [timeCount, setTimeSliderHeight])

  useEffect(() => {
    if (!map || !isMapLoaded) return
    if (map.resize) {
      map.resize()
    }
  }, [map, isMapLoaded, sidebarWidth])

  useEffect(() => {
    if (!map || !isMapLoaded) return
    try {
      ;(map as any).setProjection({ type: globeProjection ? 'globe' : 'mercator' })
    } catch (e) {
      console.warn('Projection switch not supported:', e)
    }
  }, [map, isMapLoaded, globeProjection])

  const isLoading = loadingState.loading || timeMeanLoading || timeSeriesLoading

  return (
    <div style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
      <div
        ref={mapContainer}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
        }}
      />
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 10,
            left: (sidebarWidth ?? 0) + 10,
            background: 'rgba(250,253,255,0.92)',
            border: '1px solid var(--rule)',
            borderRadius: 4,
            padding: '4px 10px',
            fontSize: '0.72rem',
            color: 'var(--teal)',
            pointerEvents: 'none',
            zIndex: 100,
          }}
        >
          {timeMeanLoading ? 'Computing time mean…' : timeSeriesLoading ? 'Loading time series…' : 'Loading…'}
        </div>
      )}
      {/* Time panel: slider + chart in one dockable container */}
      {timeCount > 1 && (
        <div
          ref={panelRef}
          style={{
            position: 'absolute',
            left: (sidebarWidth ?? 0) + 10,
            right: rightRailWidth + 10,
            bottom: 10,
            zIndex: 80,
          }}
        >
          <TimeSlider />
          <TimeSeriesChartOverlay />
        </div>
      )}
      <DatasetChip />
      <MapColorbar />
      <OpenInNewWindowButton />
      <DatasetSwitchNotice />
      <MapStatus />
    </div>
  )
}
